import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withKeys as _withKeys, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "z-10" }
const _hoisted_3 = { class: "flex flex-col h-full z-10 w-full" }
const _hoisted_4 = { class: "w-full border-b p-4" }
const _hoisted_5 = { class: "relative max-h-80 overflow-y-auto" }
const _hoisted_6 = {
  key: 0,
  class: "py-8"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "min-w-[32px] min-h-[32px] rounded-full bg-gray-300 items-center justify-center flex" }
const _hoisted_9 = { class: "flex flex-col w-[calc(100%-40px)]" }
const _hoisted_10 = {
  key: 0,
  class: "text-gray-500"
}
const _hoisted_11 = {
  key: 1,
  class: "text-gray-500 truncate"
}

import { PropType, ref, watch, computed, inject } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsLoading from "@/components/GgmsLoading.vue"
import { PlusCircleIcon } from "@heroicons/vue/solid"
import { useInboxStore } from "@/stores/inbox"
import { useLeadsStore } from "@/stores/leads"
import { ToastService } from "@/shared/services/toast"
import { InboxContact } from "@/shared/models/inbox"
import { debounce, getFullName, getInitials, isValidEmail, isValidPhoneNumber } from "@/shared/utils/helpers"


export default /*#__PURE__*/_defineComponent({
  __name: 'ContactSelect',
  props: {
    type: {
        type: String,
        default: "sms",
        validator: (value: string) => ["sms", "email"].includes(value),
    },
    recipients: {
        type: Array as PropType<InboxContact[]>,
        default: () => [],
    },
    toFieldLength: {
        type: Number,
        default: 0,
    },
    fieldName: {
        type: String,
        default: "to",
    },
},
  emits: ["close", "select"],
  setup(__props, { emit: __emit }) {

const inboxStore = useInboxStore()
const leadStore = useLeadsStore()
const toastService = new ToastService()

const props = __props

const emit = __emit

const contactOverlayTarget = inject(`contactOverlayTarget-${props.fieldName}`) as string

const menu = ref()
const plusCircleIcon = ref()
const possibleContacts = ref<InboxContact[]>([])
const isSMSType = computed(() => props.type === "sms")
const placeholder = computed(() => (isSMSType.value ? "Enter name or phone number" : "Enter name or email address"))
const isSearchingContact = ref(false)

const searchTerm = ref("")
const overlayPanelFloat = ref("right")
const overlayPanelClass = computed(() => (overlayPanelFloat.value === "right" ? "!left-0" : "!left-auto"))

function selectContact(contact: InboxContact) {
    menu.value.hide()
    emit("select", contact)
}

// select those users that are not leads or agents
function selectUnknownContact(searchTerm: string) {
    if (!searchTerm) {
        return
    }
    if (isValidEmail(searchTerm)) {
        selectContact({ email: searchTerm } as InboxContact)
        return
    }
    if (isValidPhoneNumber(searchTerm)) {
        selectContact({ phone: { number: searchTerm } } as InboxContact)
        return
    }
    toastService.addToast({
        message: "Please enter a valid email address or phone number",
        type: "error",
    })
}

function clearLeads() {
    possibleContacts.value = []
    searchTerm.value = ""
}

async function searchContacts() {
    try {
        isSearchingContact.value = true
        const startingSearchTerm = searchTerm.value

        const response = isSMSType.value
            ? await searchContactsByPhone()
            : await inboxStore.autocomplete(searchTerm.value, 20)

        // need to check if the search term has changed since the request was made, to prevent dialog from filling with data after closing and reopening it
        if (!startingSearchTerm || startingSearchTerm !== searchTerm.value) {
            possibleContacts.value = []
            return
        }

        possibleContacts.value = response.filter(
            (contact) =>
                !props.recipients.some((recipient) =>
                    isSMSType.value
                        ? recipient.phone?.number === contact.phone?.number
                        : recipient.email === contact.email
                )
        )
    } finally {
        isSearchingContact.value = false
    }
}

async function searchContactsByPhone() {
    const filters = [{ field: "phones.number", rule: "isNotEmpty" }]
    const response = await leadStore.leadService.getAll({
        start: 0,
        length: 10,
        search: searchTerm.value,
        filters,
    })

    // convert the leads to inbox contacts
    const lead = response.data?.[0]

    if (!lead?.phones?.length) {
        return [] as InboxContact[]
    }
    return lead.phones.map((phone) => {
        const fullName = getFullName(lead?.firstName, lead?.lastName)
        return { fullName, phone, _id: lead._id } as InboxContact
    })
}

function toggleMenu(event?: Event) {
    // get the X position of the plus circle icon
    const plusCircleIconX = plusCircleIcon.value.getBoundingClientRect().x
    // set the float of the overlay panel to the appropriate side based on the X position of the plus circle icon
    overlayPanelFloat.value = plusCircleIconX > window.innerWidth / 2 ? "left" : "right"

    menu.value?.toggle(event)
}

watch(
    searchTerm,
    debounce(() => {
        if (!searchTerm.value) {
            possibleContacts.value = []
            return
        }
        searchContacts()
    }, 350)
)

return (_ctx: any,_cache: any) => {
  const _component_OverlayPanel = _resolveComponent("OverlayPanel")!

  return (_openBlock(), _createElementBlock("div", {
    id: _unref(contactOverlayTarget),
    class: "h-6 w-6 relative"
  }, [
    _createVNode(_unref(PlusCircleIcon), {
      ref_key: "plusCircleIcon",
      ref: plusCircleIcon,
      class: "h-full w-full text-primary-color-600 inline-block hover:text-primary-color-700 cursor-pointer",
      onClick: toggleMenu
    }, null, 512),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_OverlayPanel, {
        ref_key: "menu",
        ref: menu,
        appendTo: `#${_unref(contactOverlayTarget)}`,
        class: _normalizeClass(["ring-1 ring-black ring-opacity-5 shadow-lg rounded-md bg-white min-w-[288px] w-full !top-4 right-1", overlayPanelClass.value]),
        onHide: clearLeads
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(GgmsInput, {
                modelValue: searchTerm.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                styleType: "secondary",
                placeholder: placeholder.value,
                small: true,
                onKeydown: _cache[1] || (_cache[1] = _withKeys(($event: any) => (selectUnknownContact(searchTerm.value)), ["enter"]))
              }, null, 8, ["modelValue", "placeholder"])
            ]),
            _createElementVNode("div", _hoisted_5, [
              (!possibleContacts.value.length && isSearchingContact.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(possibleContacts.value, (contact) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: contact._id,
                      tabindex: "0",
                      class: "z-10 rounded-md flex items-center gap-x-2 outline-primary-color-500 hover:bg-gray-50 px-4 py-3 cursor-pointer",
                      onClick: ($event: any) => (selectContact(contact))
                    }, [
                      _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(getInitials)(contact.fullName)), 1),
                      _createElementVNode("div", _hoisted_9, [
                        _createTextVNode(_toDisplayString(contact.fullName) + " ", 1),
                        (isSMSType.value && contact?.phone)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_10, " (" + _toDisplayString(contact.phone.number) + ") ", 1))
                          : (_openBlock(), _createElementBlock("p", _hoisted_11, "(" + _toDisplayString(contact.email) + ")", 1))
                      ])
                    ], 8, _hoisted_7))
                  }), 128)),
              (isSearchingContact.value)
                ? (_openBlock(), _createBlock(GgmsLoading, { key: 2 }))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _: 1
      }, 8, ["appendTo", "class"])
    ])
  ], 8, _hoisted_1))
}
}

})